import api from './api';
import { isCompanyAdmin, roleOf } from './companyRoles';

/** Last-known companies for this email, so the switcher can paint before the
 *  network returns. Not a permission source — the live request still wins. */
export const WORKSPACE_COMPANIES_CACHE_KEY = 'velox-workspace-companies';

export function mapEnterpriseRows(rows) {
  return (rows || []).map((row) => ({
    id: String(row.enterpriseId || row._id),
    name: row.name || 'Company',
    role: roleOf(row),
    isOwner: !!row.isOwner,
    isAdmin: isCompanyAdmin(row),
    onboardingCompletedAt: row.enterpriseOnboardingCompletedAt || null,
    onboardingDismissedAt: row.enterpriseOnboardingDismissedAt || null,
  }));
}

function cacheEmail() {
  return (localStorage.getItem('userEmail') || '').toLowerCase().trim();
}

export function readWorkspaceCompaniesCache() {
  try {
    const email = cacheEmail();
    if (!email) return [];
    const raw = localStorage.getItem(WORKSPACE_COMPANIES_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if ((parsed.email || '').toLowerCase() !== email) return [];
    return Array.isArray(parsed.companies) ? parsed.companies : [];
  } catch {
    return [];
  }
}

export function writeWorkspaceCompaniesCache(companies) {
  try {
    const email = cacheEmail();
    if (!email) return;
    localStorage.setItem(
      WORKSPACE_COMPANIES_CACHE_KEY,
      JSON.stringify({ email, companies })
    );
  } catch {
    // A private-mode storage failure must not break sign-in.
  }
}

export function clearWorkspaceCompaniesCache() {
  try {
    localStorage.removeItem(WORKSPACE_COMPANIES_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

let inflight = null;
let inflightToken = null;

/**
 * One shared request for the signed-in user's companies.
 *
 * Login starts this before routing to /home so the workspace provider can
 * join the same promise instead of waiting on a second round trip.
 */
export function prefetchWorkspaceCompanies() {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve([]);
  if (inflight && inflightToken === token) return inflight;

  inflightToken = token;
  inflight = api
    .get('/api/enterprise/my-enterprises')
    .then((res) => {
      const list = mapEnterpriseRows(res.data?.enterprises || []);
      writeWorkspaceCompaniesCache(list);
      return list;
    })
    .catch(() => {
      // Keep the last good list. An empty flash is worse than a briefly stale one.
      return readWorkspaceCompaniesCache();
    })
    .finally(() => {
      if (inflightToken === token) {
        inflight = null;
        inflightToken = null;
      }
    });
  return inflight;
}

/** Jest only — a hung mock must not leak into the next test. */
export function resetWorkspaceCompaniesForTests() {
  inflight = null;
  inflightToken = null;
}
